/**
 * Génère et imprime le reçu officiel d'une opération de caisse.
 *
 * Mise en page A4 portrait en deux exemplaires identiques (client / agence)
 * séparés par une ligne de découpe, chacun avec le montant en chiffres et en lettres,
 * le détail du billetage et les zones de signature.
 */
import { TransactionResponseDTO } from '../../../application/dto/TransactionResponseDTO';
import { formatMoney } from '../../../shared/utils/formatters';
import { montantEnLettres } from '../../../shared/utils/montantEnLettres';
import logoUnacoopec from '../../assets/logo-unacoopec.jpg';

const esc = (v: unknown) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const FORMAT_JOUR = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

const FORMAT_DATE = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** « Plateau » → « Agence Plateau » ; « Agence Plateau » ou « COOPEC Attécoubé » restent tels quels. */
export function libelleAgence(agence?: string | null) {
  if (!agence || !agence.trim()) return 'Réseau des COOPEC';
  const a = agence.trim();
  return /^(agence|coopec|unacoopec)\b/i.test(a) ? a : `Agence ${a}`;
}

export function libelleOperation(t: TransactionResponseDTO) {
  return t.type === 'WITHDRAWAL' ? "Retrait d'espèces" : "Versement d'espèces";
}

/** Versement : qui a remis les espèces (le titulaire ou un tiers identifié par sa pièce). */
export function lignesDeposant(t: TransactionResponseDTO): [string, string][] {
  const d = t.deposant;
  if (t.type !== 'DEPOSIT' || !d) return [];
  if (!d.tiers) return [['Déposant', 'Le titulaire du compte']];
  const lignes: [string, string][] = [['Déposant', d.nom || '—']];
  if (d.pieceType || d.pieceNumero) lignes.push(["Pièce d'identité", [d.pieceType, d.pieceNumero && `n° ${d.pieceNumero}`].filter(Boolean).join(' ')]);
  if (d.contact) lignes.push(['Contact', d.contact]);
  return lignes;
}

export function lignesBilletage(t: TransactionResponseDTO) {
  return Object.entries(t.denomination ?? {})
    .map(([coupure, nombre]) => ({ coupure: Number(coupure), nombre: Number(nombre) }))
    .filter((l) => l.nombre > 0)
    .sort((a, b) => b.coupure - a.coupure);
}

function exemplaire(t: TransactionResponseDTO, libelleExemplaire: string, logo: string) {
  // Duplicata depuis l'historique CBS : seule la date est connue, pas l'heure
  const date = t.duplicata ? FORMAT_JOUR.format(new Date(t.createdAt)) : FORMAT_DATE.format(new Date(t.createdAt));
  const retrait = t.type === 'WITHDRAWAL';
  const billets = lignesBilletage(t);
  const totalBillets = billets.reduce((s, l) => s + l.coupure * l.nombre, 0);
  const signataires = t.signataires ?? [];

  const lignes: [string, string][] = [
    ['Compte', t.accountNumber],
    ['Titulaire', t.clientName],
    ...(t.agence ? [['Agence', t.agence] as [string, string]] : []),
    ...lignesDeposant(t),
    ...(signataires.length > 1
      ? signataires.map((s) => [`Signataire ${s.ordre}`, `${s.nom}${s.carteMasquee ? ` — carte ${s.carteMasquee}` : ''}`] as [string, string])
      : signataires.length === 1 && signataires[0].carteMasquee
        ? [['Carte utilisée', signataires[0].carteMasquee] as [string, string]]
        : []),
    ...(t.destination ? [['Motif', t.destination] as [string, string]] : []),
    ['Caissier', `${t.cashierName}${t.cashierId ? ` (${t.cashierId})` : ''}`],
  ];

  return `
  <section class="recu">
    <header>
      <img src="${esc(logo)}" alt="UNACOOPEC" class="logo" />
      <div class="entete">
        <div class="org">UNACOOPEC</div>
        <div class="sous">${esc(libelleAgence(t.agence))}</div>
      </div>
      <div class="exemplaire">${esc(libelleExemplaire)}</div>
    </header>

    <div class="titre">
      <h1>Reçu de ${retrait ? "retrait d'espèces" : "versement d'espèces"}</h1>
      <div class="ref">
        <div><span>N°</span> ${esc(t.reference)}</div>
        <div><span>Date</span> ${esc(date)}</div>
      </div>
    </div>

    <div class="corps">
      <table class="infos">
        ${lignes.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('')}
      </table>

      <div class="montant">
        <div class="lib">Montant ${retrait ? 'retiré' : 'versé'}</div>
        <div class="chiffres">${esc(formatMoney(t.amount))}</div>
        <div class="lettres">${esc(montantEnLettres(t.amount))}</div>
      </div>
    </div>

    ${
      billets.length > 0
        ? `<table class="billets">
        <thead><tr><th>Coupure</th><th>Nombre</th><th>Montant</th></tr></thead>
        <tbody>
          ${billets
            .map(
              (l) =>
                `<tr><td>${esc(formatMoney(l.coupure))}</td><td>${l.nombre}</td><td>${esc(formatMoney(l.coupure * l.nombre))}</td></tr>`,
            )
            .join('')}
        </tbody>
        <tfoot><tr><td>Total</td><td>${billets.reduce((s, l) => s + l.nombre, 0)}</td><td>${esc(formatMoney(totalBillets))}</td></tr></tfoot>
      </table>`
        : ''
    }

    <div class="signatures">
      <div><span>Le caissier</span></div>
      <div><span>${retrait ? 'Le client' : 'Le déposant'}</span></div>
    </div>

    <footer>
      Opération ${retrait ? 'authentifiée par carte et empreinte digitale' : 'enregistrée'} · Réf. BIOTRANSAC ${esc(t.biotransacRef)}
      · Conservez ce reçu, il vous sera demandé en cas de réclamation.
    </footer>
  </section>`;
}

export function genererHtmlRecu(t: TransactionResponseDTO): string {
  const logo = new URL(logoUnacoopec, window.location.href).href;
  return `<!doctype html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<title>${esc(libelleOperation(t))} ${esc(t.reference)}</title>
<style>
  @page { size: A4 portrait; margin: 10mm 12mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: "Segoe UI", Arial, sans-serif; color: #111827; font-size: 10.5pt;
         -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .recu { height: 134mm; padding: 3mm 2mm; display: flex; flex-direction: column; gap: 2.2mm; overflow: hidden; break-inside: avoid; }
  header { display: flex; align-items: center; gap: 4mm; border-bottom: 2px solid #0B677E; padding-bottom: 2mm; }
  .logo { height: 11mm; width: auto; object-fit: contain; }
  .entete { flex: 1; }
  .org { font-size: 15pt; font-weight: 700; color: #0B677E; letter-spacing: .5px; }
  .sous { font-size: 9pt; color: #4b5563; }
  .exemplaire { font-size: 8pt; text-transform: uppercase; letter-spacing: 1px; border: 1px solid #0B677E;
                color: #0B677E; padding: 1mm 2.5mm; border-radius: 1mm; font-weight: 600; }
  .titre { display: flex; justify-content: space-between; align-items: flex-end; gap: 4mm; }
  h1 { margin: 0; font-size: 13pt; text-transform: uppercase; letter-spacing: .5px; }
  .ref { text-align: right; font-size: 9pt; line-height: 1.5; }
  .ref span { color: #6b7280; margin-right: 1.5mm; }
  .corps { display: flex; gap: 5mm; align-items: stretch; }
  .infos { flex: 1.3; border-collapse: collapse; font-size: 9.5pt; }
  .infos th { text-align: left; font-weight: 500; color: #6b7280; padding: .7mm 3mm .7mm 0; white-space: nowrap; vertical-align: top; width: 28mm; }
  .infos td { padding: .7mm 0; font-weight: 600; }
  .montant { flex: 1; border: 1.5px solid #0B677E; border-radius: 2mm; padding: 3mm; background: #f0f7f9;
             display: flex; flex-direction: column; justify-content: center; }
  .montant .lib { font-size: 8.5pt; color: #0B677E; text-transform: uppercase; letter-spacing: .5px; font-weight: 600; }
  .montant .chiffres { font-size: 18pt; font-weight: 800; margin: 1mm 0; }
  .montant .lettres { font-size: 9pt; font-style: italic; color: #374151; }
  .billets { width: 100%; border-collapse: collapse; font-size: 8pt; }
  .billets th:not(:first-child), .billets td:not(:first-child) { width: 30mm; }
  .billets th, .billets td { border: 1px solid #d1d5db; padding: .5mm 2mm; text-align: right; }
  .billets th { background: #f3f4f6; font-weight: 600; }
  .billets th:first-child, .billets td:first-child { text-align: left; }
  .billets tfoot td { font-weight: 700; background: #f9fafb; }
  .signatures { display: flex; gap: 10mm; margin-top: auto; }
  .signatures div { flex: 1; height: 13mm; border: 1px dashed #9ca3af; border-radius: 1.5mm; padding: 1.5mm 2.5mm; }
  .signatures span { font-size: 8.5pt; color: #6b7280; }
  footer { font-size: 7.5pt; color: #6b7280; border-top: 1px solid #e5e7eb; padding-top: 1.5mm; }
  .decoupe { border-top: 1px dashed #9ca3af; margin: 2mm 0; position: relative; height: 0; }
  .decoupe::after { content: "✂  découper ici"; position: absolute; left: 50%; top: -2.3mm; transform: translateX(-50%);
                    background: #fff; padding: 0 2mm; font-size: 7pt; color: #9ca3af; }
</style>
</head>
<body>
  ${t.duplicata
    ? exemplaire(t, 'Duplicata', logo)
    : `${exemplaire(t, 'Exemplaire client', logo)}
  <div class="decoupe"></div>
  ${exemplaire(t, 'Exemplaire agence', logo)}`}
</body>
</html>`;
}

/**
 * Imprime le reçu via une iframe masquée (pas de fenêtre pop-up bloquée par le navigateur).
 * L'impression attend le chargement du logo.
 */
export function imprimerRecu(t: TransactionResponseDTO): Promise<void> {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (!doc || !win) {
      iframe.remove();
      resolve();
      return;
    }

    let imprime = false;
    const lancer = () => {
      if (imprime) return;
      imprime = true;
      win.focus();
      win.print();
      // Laisser le temps à la boîte d'impression de se fermer avant de retirer l'iframe
      setTimeout(() => {
        iframe.remove();
        resolve();
      }, 1000);
    };

    doc.open();
    doc.write(genererHtmlRecu(t));
    doc.close();

    const images = Array.from(doc.images);
    const enAttente = images.filter((img) => !img.complete);
    if (enAttente.length === 0) {
      setTimeout(lancer, 50);
    } else {
      let restantes = enAttente.length;
      enAttente.forEach((img) => {
        const fin = () => {
          restantes -= 1;
          if (restantes === 0) lancer();
        };
        img.addEventListener('load', fin, { once: true });
        img.addEventListener('error', fin, { once: true });
      });
      setTimeout(lancer, 2500); // sécurité : imprimer même si le logo ne charge pas
    }
  });
}
